import React, { useMemo } from 'react';
import Icon from './Icon';
import RecipeCard, { isCompleteCard, RecipeCardData } from './RecipeCard';
import { deriveCard } from '../utils/deriveCard';
import { ingredientAmount, splitInstructions } from '../utils/recipeFormat';

export interface RecipeLike {
  name: string;
  cuisine: string;
  difficulty: string;
  prep_time: number;
  cook_time: number;
  ingredients: { name: string; quantity: number; unit: string }[];
  instructions: string;
  card?: RecipeCardData | null;
}

interface Props {
  recipe: RecipeLike;
  onClose: () => void;
  // When provided, the modal is being opened from the Cook flow -- shows
  // a "Mark as Cooked" action once the user's done following the recipe.
  onMarkCooked?: () => void;
  marking?: boolean;
}

// AI-discovered recipes carry a card built from their structured steps;
// everything else (imported, manual, older) gets one formatted from the
// instruction text. Either way it's all-or-nothing: a recipe whose card
// wouldn't account for every ingredient shows no card, just the list and
// steps below.
const useCard = (recipe: RecipeLike): RecipeCardData | null =>
  useMemo(() => {
    if (recipe.card) return isCompleteCard(recipe.card, recipe.ingredients.length) ? recipe.card : null;
    return deriveCard(recipe.ingredients, recipe.instructions);
  }, [recipe.card, recipe.ingredients, recipe.instructions]);

const RecipeDetailModal: React.FC<Props> = ({ recipe, onClose, onMarkCooked, marking }) => {
  const card = useCard(recipe);
  const steps = splitInstructions(recipe.instructions);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>

        <div className="recipe-hero">
          <h2>{recipe.name}</h2>
          <div className="recipe-tags">
            <span className="tag"><Icon name="globe" className="icon icon-sm" /> {recipe.cuisine}</span>
            <span className="tag"><Icon name="bars" className="icon icon-sm" /> {recipe.difficulty}</span>
            <span className="tag"><Icon name="clock" className="icon icon-sm" /> {recipe.prep_time + recipe.cook_time} min</span>
          </div>
        </div>

        {card && (
          <div className="recipe-section">
            <h3>At a Glance</h3>
            <RecipeCard name={recipe.name} card={card} ingredients={recipe.ingredients} />
          </div>
        )}

        <div className="recipe-section">
          <h3>Ingredients</h3>
          <ul className="ingredients-list">
            {recipe.ingredients.map((ing, idx) => (
              <li key={idx}>
                <strong>{ingredientAmount(ing)}</strong> {ing.name}
              </li>
            ))}
          </ul>
        </div>

        <div className="recipe-section">
          <h3>Instructions</h3>
          <ol className="recipe-steps">
            {steps.map((step, idx) => (
              <li key={idx}>{step}</li>
            ))}
          </ol>
        </div>

        {onMarkCooked && (
          <div className="form-actions">
            <button type="button" onClick={onMarkCooked} disabled={marking} className="btn-primary">
              <Icon name="check" className="icon icon-sm" />
              {marking ? 'Marking as Cooked...' : 'Mark as Cooked'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipeDetailModal;
